// ---------------------------------------------------------------------------
// The browser side of the CLIENT PORTAL. V24.
//
// A SEPARATE FILE BECAUSE IT IS A SEPARATE NAMESPACE, and 18-client-portal.md
// §6.3 gives the reason that decides it: "the response shapes genuinely differ.
// /v1/events/{id}/readiness returns blockers[].worker_id and the full
// explanation array. The portal response must not, BY CONSTRUCTION rather than
// by a filter someone can forget."
//
// The types below are that construction on this side of the wire. There is no
// `worker_id` on a blocker here, no charge rate, no budget, no score and no
// ranking — not because a screen declines to render one, but because the type
// has no field for it and the handler that would have to add one lives in a file
// whose projection is fixed in SQL.
//
// NO VOCABULARY IS SPELLED IN THIS FILE. A seat state arrives as a code AND its
// word from app.portal_seat_state; a blocker arrives as a code AND its client
// sentence from the API. Absolute rule 4 — and the trap HANDOFF.md §6 records
// twice, where a client-facing word collided with an assignment status because
// somebody typed it in TypeScript rather than reading it from the database.
// ---------------------------------------------------------------------------

import { mutate, read } from "./console";
import type { TrendPoint } from "../system/wire-types";
export type { TrendPoint } from "../system/wire-types";

/** §4's S1 zone 1. The plain-language state, never a colour. */
export type ReadinessState = { label: string; code: string; tone: string };

/** §4's S1 zone 2. `owner` is the agency, always, and it arrives from the server. */
export type PortalBlocker = {
  code: string;
  sentence: string;
  /** S145. The template and values the sentence was filled from, for the client's catalogue. */
  sentence_key?: string;
  params?: Record<string, string | number>;
  owner: string;
  score_impact: number;
};

/** §4's S1 zone 3, and `annotation` is §V17.4's anti-gaming rule made visible. */

export type PortalReadiness = {
  event: {
    event_id: string;
    name: string;
    starts_at: string;
    ends_at: string;
    venue_name: string | null;
  };
  computed_at: string | null;
  score: number | null;
  state: ReadinessState;
  coverage: { seats: number; covered: number };
  critical_posts: { total: number; dark: number; dark_places: string[] };
  blockers: PortalBlocker[];
  trend: TrendPoint[];
};

export type ProgrammeEvent = {
  event_id: string;
  name: string;
  starts_at: string;
  ends_at: string;
  venue_name: string | null;
  status: string;
  roster: { version_no: number; published_at: string | null; seat_count: number; covered_count: number } | null;
  open_change_requests: number;
  operational_window_open: boolean;
};

export type RosterPost = {
  seq: number;
  post_name: string;
  zone: string | null;
  starts_at: string;
  ends_at: string;
  is_reserve: boolean;
  is_supervisor: boolean;
  worker_name: string | null;
  state: { code: string; label: string };
};

export type PortalRoster = {
  roster_version: { version_no: number; published_at: string } | null;
  live_as_at: string | null;
  exported_as: string | null;
  posts: RosterPost[];
};

export type PortalChange = {
  change: string;
  sentence: string;
  sentence_key?: string;
  params?: Record<string, string | number>;
  post_name: string;
  zone: string | null;
  starts_at: string;
  ends_at: string;
  is_scope_reduction: boolean;
};

export type PortalDocuments = {
  briefings: { headline: string; count: number }[];
  credentials: { headline: string; headline_key?: string; params?: Record<string, string | number>; exceptions: number };
  /** 0230 (S107). The signed risk assessment in force per scope: version and sign-off day only. */
  risk_assessments: {
    headline: string;
    headline_key?: string;
    params?: Record<string, string | number>;
    version: number;
    signed_at: string | null;
  }[];
  proof_packs: { proof_pack_id: string | null; issued_at: string | null }[];
  proof_pack_explanation: string;
};

export type PortalApprovalItem = {
  subject_type: string;
  subject_id: string;
  headline: string;
  variance_minutes: number | null;
  /** §4's S4 rule 4: null is SILENCE, not pending and not rejected. */
  decision: string | null;
  /**
   * V25. The word beside the id, from the same query. The screen keeps its
   * client-side lookup as the fallback, so a decision the server has not labelled
   * still reads as something rather than as nothing.
   */
  decision_label: string | null;
  decided_at: string | null;
};

export type PortalApprovals = {
  notice: string;
  may_decide: boolean;
  items: PortalApprovalItem[];
};

const event = (base: string, id: string, leaf: string, query = "") =>
  read(base, `/v1/portal/events/${encodeURIComponent(id)}/${leaf}${query}`);

/** What a client may say. Read, never spelled — see the note on `decide` below. */
export type ClientDecision = {
  id: string;
  label: string;
  is_positive: boolean;
  description: string;
};

/** The client's seat vocabulary. Read, never spelled. */
export type PortalSeatState = {
  id: string;
  label: string;
  rank: number;
  derived_from: string;
  description: string;
};

export const portal = {
  seatStates: (base: string) => read(base, "/v1/reference/portal-seat-states"),
  decisions: (base: string) => read(base, "/v1/reference/client-decisions"),
  /** S6. The programme, and the only portal read that is not about one event. */
  events: (base: string) => read(base, "/v1/portal/events"),
  readiness: (base: string, id: string) => event(base, id, "readiness"),
  /**
   * S2. `format` is the EXPORT and it is a query parameter because it changes
   * what the server records, not what it returns: §8 check 9 requires every
   * export to write a client_portal_view row, so an export that were done in the
   * browser from an already-fetched list would leave no trace at all.
   */
  roster: (base: string, id: string, format?: "csv" | "xlsx") =>
    event(base, id, "roster", format ? `?export=${format}` : ""),
  /**
   * S3. `since` is a ROSTER VERSION NUMBER, and omitting it means "since you
   * last looked" — which the server answers from client_user.last_seen_changes_at
   * rather than from anything this file knows.
   */
  changes: (base: string, id: string, sinceVersion?: number) =>
    event(base, id, "changes", sinceVersion === undefined ? "" : `?since=${sinceVersion}`),
  documents: (base: string, id: string) => event(base, id, "documents"),
  approvals: (base: string, id: string) => event(base, id, "approvals"),

  /**
   * S4, and the only two writes on this surface.
   *
   * `decision` is NOT an enum here, and the screen does not spell one either — it
   * renders one button per row of GET /v1/reference/client-decisions.
   *
   * THE FIRST VERSION DID SPELL ONE AND IT WAS ALREADY WRONG. It posted the word
   * an agency uses when it accepts a worker's hours — which 0069 had renamed out
   * of the client vocabulary for precisely that reason, because that word is an
   * app.assignment_status id and no client-facing word may be one. The button
   * would have posted a decision that does not exist and the foreign key would
   * have refused it. Two inherited guards caught it before a browser did, which
   * is the whole argument for a rule about taxonomies being enforced rather than
   * remembered.
   *
   * THE WORD IS NOT REPEATED IN THIS COMMENT EITHER, and that is the same guard
   * working twice: quoting it here failed verify-V13.sh, which cannot tell prose
   * from code and must not try. The rule for a SHARED guard is to rewrite the
   * prose rather than weaken the check for every slice that depends on it.
   */
  decide: (
    base: string,
    payload: { event_id: string; subject_type: string; subject_id: string; decision: string; note?: string },
  ) => mutate("POST", base, "/v1/portal/approvals", payload),

  // Sweep S14: what this client asked for on an event, and the agency's answer.
  myChangeRequests: (base: string, eventId: string) =>
    read(base, `/v1/portal/events/${encodeURIComponent(eventId)}/change-requests`),
  requestChange: (base: string, payload: { event_id: string; body: string; requirement_id?: string }) =>
    mutate("POST", base, "/v1/portal/change-requests", payload),

  /**
   * D12. The workers this client may name, by opaque reference. NOT about one
   * event — a reference outlives the roster it was minted from, which is the
   * point of asking about a future date.
   */
  workers: (base: string) => read(base, "/v1/portal/workers"),

  /**
   * D12. A POST, and the window is a BODY rather than a query string, so a
   * worker reference and the dates somebody is being checked against never reach
   * an access log. `is_free` comes back as `true`, `false` or **`null`**, and
   * null is "you may not ask" rather than "busy" — the screen must not collapse
   * the three into two.
   */
  availability: (base: string, ref: string, window: { from: string; to: string }) =>
    mutate("POST", base, `/v1/portal/workers/${encodeURIComponent(ref)}/availability`, window),
};

/** D12. Three answers, and the screen renders a different sentence for each. */
export type PortalWorker = { ref: string; name: string; availability_askable: boolean };
export type PortalAvailability = {
  ref: string;
  from: string;
  to: string;
  is_free: boolean | null;
  availability_askable: boolean;
  notice: string | null;
};
